import { useEffect, useState } from "react";

import { ScreenSpinner, useToastStore } from "@/components";
import { ChangeNotificationsAlert } from "@/components/modals/change-notifications";
import { useSaveProfile } from "@/domains/profile/mutations/use-save-profile";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { getSmsResubscribeMessage } from "@/domains/profile/utils/sms-resubscribe";
import { Toggle } from "@/ui/toggle";

const NOTIFICATION_TYPE = {
  sms: "sms",
  email: "email",
} as const;

export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

export const NotificationSettings = () => {
  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });
  const { data: profile } = useProfile();

  const [emailToggle, setEmailToggle] = useState(() => {
    return Boolean(profile?.emailsEnabledAt);
  });
  const [smsToggle, setSmsToggle] = useState(() => {
    return Boolean(profile?.smsEnabledAt);
  });
  const [modalType, setModalType] = useState<NotificationType>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [smsResubscribeMessage, setSmsResubscribeMessage] = useState<string>();

  useEffect(() => {
    if (profile) {
      setEmailToggle(Boolean(profile.emailsEnabledAt));
      setSmsToggle(Boolean(profile.smsEnabledAt));
    }
  }, [profile]);

  const { isPending, mutate: saveProfile } = useSaveProfile();

  const modalTypeAction = {
    email: () => {
      return setEmailToggle((v) => {
        return !v;
      });
    },
    sms: () => {
      return setSmsToggle((v) => {
        return !v;
      });
    },
  };

  const mutationOptions = (value: boolean, isSms: boolean) => {
    return {
      onSuccess: () => {
        if (isSms) {
          setSmsResubscribeMessage(undefined);
        }
        void pushToast({
          message: `${isSms ? "SMS" : "Email"} notifications were turned ${value ? "on" : "off"}`,
          type: "success",
        });
      },
      onError: (error: unknown) => {
        const resubscribeMessage = getSmsResubscribeMessage(error);

        if (resubscribeMessage) {
          setSmsToggle(false);
          setSmsResubscribeMessage(resubscribeMessage);

          if (!isSms) {
            onSubmitEmail(value, false);
          }

          return;
        }

        if (isSms) {
          setSmsToggle(!value);
        } else {
          setEmailToggle(!value);
        }

        void pushToast({
          message: `Failed to change ${isSms ? "SMS" : "email"} notifications settings`,
          type: "error",
        });
      },
      onSettled: () => {
        setIsOpenModal(false);
        setTimeout(() => {
          setModalType(undefined);
        }, 300);
      },
    };
  };

  const onSubmitSMS = (value: boolean) => {
    saveProfile(
      { sms_enabled: value, emails_enabled: emailToggle },
      { ...mutationOptions(value, true) },
    );
  };

  const onSubmitEmail = (value: boolean, smsEnabled: boolean = smsToggle): void => {
    saveProfile(
      { emails_enabled: value, sms_enabled: smsEnabled },
      { ...mutationOptions(value, false) },
    );
  };

  const onModalConfirm = () => {
    if (modalType === NOTIFICATION_TYPE.sms) {
      onSubmitSMS(false);
    } else {
      onSubmitEmail(false);
    }
  };

  const openModal = (type: NotificationType) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  const closeModal = () => {
    if (modalType) {
      modalTypeAction[modalType]();
      setIsOpenModal(false);
      setTimeout(() => {
        setModalType(undefined);
      }, 300);
    }
  };

  if (isPending) {
    return <ScreenSpinner className="pt-0 sm:pt-16" />;
  }

  return (
    <>
      <div className="mt-6 flex flex-col gap-6 text-content sm:mt-0">
        <h2 className="hidden font-semibold sm:block">Notifications</h2>
        <p>
          Notifications are an essential part of the EO experience. They can include vital,
          personalized instructions and notes from our clinical team. They can also include
          reminders to review care plans and recommended products and provide feedback on your care.
        </p>
        <section className="flex flex-1 flex-col gap-5 overflow-auto pt-1.5 pr-2">
          <div className="flex items-center justify-between border-b pb-4">
            <h3>Email</h3>
            <div className="flex items-center gap-4">
              <span>{emailToggle ? "On" : "Off"}</span>
              <Toggle
                checked={emailToggle}
                onChange={(value) => {
                  setEmailToggle(value);
                  if (!value) {
                    openModal(NOTIFICATION_TYPE.email);
                  } else {
                    onSubmitEmail(true);
                  }
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-3 border-b pb-4">
            <div className="flex items-center justify-between">
              <h3>Sms</h3>
              <div className="flex items-center gap-4">
                <span>{smsToggle ? "On" : "Off"}</span>
                <Toggle
                  checked={smsToggle}
                  onChange={(value) => {
                    setSmsToggle(value);
                    if (!value) {
                      openModal(NOTIFICATION_TYPE.sms);
                    } else {
                      onSubmitSMS(true);
                    }
                  }}
                />
              </div>
            </div>
            {smsResubscribeMessage ? (
              <p
                className="rounded-lg border border-warning-line bg-warning-surface px-3 py-2 text-sm text-content"
                role="status"
              >
                {smsResubscribeMessage}
              </p>
            ) : null}
          </div>
        </section>
      </div>
      <ChangeNotificationsAlert
        isOpen={isOpenModal}
        isSMS={modalType === NOTIFICATION_TYPE.sms}
        onClose={closeModal}
        onConfirm={onModalConfirm}
      />
    </>
  );
};
